import debounce from 'lodash.debounce';
import { temporal } from 'zundo';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

import { PlanningState, initialPlanningState } from '../types/planning';
import { migratePersistedState } from '../utils/migrations';
import { initializeNarrativeState, isSegmentUnlocked } from '../utils/narrativeHelpers';
import {
  recoverState,
  validateNarrativeState,
  validatePlanningState,
} from '../utils/stateValidation';
import { PersistedState, STATE_VERSION, isPersistedState } from '../utils/versioning';
import { deserializeNarrativeState, serializeNarrativeState } from '../utils/worldStateSerializer';

import {
  FlowSlice,
  createFlowSlice,
  createInitialFlowState,
  deserializeFlowState,
  serializeFlowState,
} from './slices/flowSlice';
import { NarrativeSlice, createNarrativeSlice } from './slices/narrativeSlice';
import { PlanningSlice, createPlanningSlice } from './slices/planningSlice';
import { ProjectsSlice, createProjectsSlice } from './slices/projectsSlice';
import { UISlice, createUISlice } from './slices/uiSlice';

// Combined store type
export type AppStore = PlanningSlice &
  NarrativeSlice &
  UISlice &
  FlowSlice &
  ProjectsSlice & {
    // Dirty state tracking
    hasUnsavedChanges: boolean;
    markDirty: () => void;
    clearDirty: () => void;
    // Computed values
    isSegmentUnlocked: (segmentId: string) => boolean;

    // Global actions
    clearAllState: () => void;
    validateState: () => { isValid: boolean; results: any };
    repairState: () => { planningRepaired: boolean; narrativeRepaired: boolean };
    hydrateFromUserData: (userId: string) => Promise<boolean>;
  };

// Create initial state first to avoid circular dependencies
const createInitialState = (set: any, get: any, api: any) => ({
  ...createPlanningSlice(set, get, api),
  ...createNarrativeSlice(set, get, api),
  ...createUISlice(set, get, api),
  ...createFlowSlice(set, get, api),
  ...createProjectsSlice(set, get, api),
  hasUnsavedChanges: false,
});

// Debounced helper to persist planning state to localStorage
const savePlanningStateDebounced = debounce((state: any) => {
  try {
    localStorage.setItem('planningState', JSON.stringify(state));
  } catch (e) {
    console.error('Failed to save planning state', e);
  }
}, 500);

// Create the store with all middleware
export const useAppStore = create<AppStore>()(
  devtools(
    persist(
      temporal(
        (set, get, api) => ({
          // Create initial state
          ...createInitialState(set, get, api),

          // Computed values
          isSegmentUnlocked: (segmentId: string) => {
            const state = get();
            return isSegmentUnlocked(state.narrativeState, segmentId);
          },

          // Global actions
          clearAllState: () => {
            set(() => createInitialState(set, get, api));
            // Clear localStorage
            localStorage.removeItem('planningState');
            localStorage.removeItem('app-store');
            localStorage.removeItem('app-projects');
            localStorage.removeItem('app-current-project-id');
            // Reset hash
            window.location.hash = '';
          },

          // State validation and recovery
          validateState: () => {
            const state = get();
            const results = {
              planning: validatePlanningState(state.planningState),
              narrative: validateNarrativeState(state.narrativeState),
            };

            // Log any issues found
            if (results.planning.warnings.length > 0) {
              console.warn('Planning state validation warnings:', results.planning.warnings);
            }
            if (results.planning.errors.length > 0) {
              console.error('Planning state validation errors:', results.planning.errors);
            }
            if (results.narrative.warnings.length > 0) {
              console.warn('Narrative state validation warnings:', results.narrative.warnings);
            }
            if (results.narrative.errors.length > 0) {
              console.error('Narrative state validation errors:', results.narrative.errors);
            }

            return {
              isValid: results.planning.isValid && results.narrative.isValid,
              results,
            };
          },

          repairState: () => {
            const state = get();
            const planningResult = validatePlanningState(state.planningState);
            const narrativeResult = validateNarrativeState(state.narrativeState);

            if (!planningResult.isValid && planningResult.sanitizedData) {
              console.warn('Repairing corrupted planning state');
              set({ planningState: planningResult.sanitizedData });
            }

            if (!narrativeResult.isValid && narrativeResult.sanitizedData) {
              console.warn('Repairing corrupted narrative state');
              set({ narrativeState: narrativeResult.sanitizedData });
            }

            return {
              planningRepaired: !planningResult.isValid,
              narrativeRepaired: !narrativeResult.isValid,
            };
          },

          hydrateFromUserData: async (userId: string): Promise<boolean> => {
            console.log('[AppStore] Hydrating from user-specific data for:', userId);

            try {
              const userStoreData = localStorage.getItem(`app-store-${userId}`);
              const userProjectsData = localStorage.getItem(`app-projects-${userId}`);
              const userProjectId = localStorage.getItem(`app-current-project-id-${userId}`);
              const userPlanningData = localStorage.getItem(`planningState-${userId}`);

              let hasRestoredData = false;

              // Restore store data with validation
              if (userStoreData) {
                try {
                  const parsedStoreData = JSON.parse(userStoreData);

                  // Apply the same validation and migration logic as the persist middleware
                  if (
                    !isPersistedState(parsedStoreData) ||
                    parsedStoreData.version !== STATE_VERSION
                  ) {
                    const migratedData = migratePersistedState(
                      parsedStoreData as unknown as PersistedState
                    );
                    Object.assign(parsedStoreData, migratedData);
                  }

                  // Deserialize flow state if needed
                  if (parsedStoreData.flow && !parsedStoreData.flow.completedSteps?.has) {
                    try {
                      parsedStoreData.flow = deserializeFlowState(parsedStoreData.flow as any);
                    } catch (e) {
                      console.error('[AppStore] Failed to deserialize user flow state:', e);
                      parsedStoreData.flow = createInitialFlowState();
                    }
                  }

                  // Validate and restore states
                  if (parsedStoreData.planningState) {
                    const validatedPlanning = recoverState(
                      parsedStoreData.planningState,
                      validatePlanningState,
                      'user planning state'
                    );
                    parsedStoreData.planningState = validatedPlanning;
                  }

                  if (parsedStoreData.narrativeState) {
                    const validatedNarrative = recoverState(
                      parsedStoreData.narrativeState,
                      validateNarrativeState,
                      'user narrative state'
                    );
                    parsedStoreData.narrativeState = validatedNarrative;
                  }

                  // Apply the validated data to the store
                  set((state) => ({
                    ...state,
                    ...parsedStoreData,
                  }));

                  hasRestoredData = true;
                  console.log('[AppStore] Successfully restored user store data');
                } catch (error) {
                  console.error('[AppStore] Failed to parse user store data:', error);
                }
              }

              // Restore projects data if available
              if (userProjectsData || userProjectId) {
                try {
                  if (userProjectsData) {
                    const parsedProjects = JSON.parse(userProjectsData);

                    // Validate project data and fix dates
                    Object.values(parsedProjects).forEach((project: any) => {
                      if (project.flowState && !project.flowState.completedSteps?.has) {
                        try {
                          project.flowState = deserializeFlowState(project.flowState as any);
                        } catch (e) {
                          console.error('[AppStore] Failed to deserialize project flow state:', e);
                          project.flowState = createInitialFlowState();
                        }
                      }

                      // Ensure dates are properly converted
                      project.createdAt = project.createdAt
                        ? new Date(project.createdAt)
                        : new Date();
                      project.lastModified = project.lastModified
                        ? new Date(project.lastModified)
                        : new Date();

                      // Validate dates
                      if (isNaN(project.createdAt.getTime())) project.createdAt = new Date();
                      if (isNaN(project.lastModified.getTime())) project.lastModified = new Date();
                    });

                    set((state) => ({ ...state, projects: parsedProjects }));
                    hasRestoredData = true;
                  }

                  if (userProjectId) {
                    set((state) => ({ ...state, currentProjectId: userProjectId }));
                  }

                  console.log('[AppStore] Successfully restored user projects data');
                } catch (error) {
                  console.error('[AppStore] Failed to parse user projects data:', error);
                }
              }

              // Restore planning data as fallback
              if (userPlanningData && !hasRestoredData) {
                try {
                  const parsedPlanning = JSON.parse(userPlanningData);
                  const validatedPlanning = recoverState(
                    parsedPlanning,
                    validatePlanningState,
                    'user planning data fallback'
                  );

                  set((state) => ({ ...state, planningState: validatedPlanning as PlanningState }));
                  hasRestoredData = true;
                  console.log('[AppStore] Successfully restored user planning data as fallback');
                } catch (error) {
                  console.error('[AppStore] Failed to parse user planning data:', error);
                }
              }

              return hasRestoredData;
            } catch (error) {
              console.error('[AppStore] Failed to hydrate from user data:', error);
              return false;
            }
          },
          markDirty: () => set({ hasUnsavedChanges: true }),
          clearDirty: () => set({ hasUnsavedChanges: false }),
        }),
        {
          // Temporal (undo/redo) configuration
          limit: 50,
          onSave: (state) => {
            // Debounced save of planning state to localStorage
            if (state?.planningState) {
              savePlanningStateDebounced(state.planningState);
            }
          },
          // Only track changes to data, not functions
          // Note: flow state is excluded from undo/redo tracking
          partialize: (state) => {
            const { planningState, narrativeState, hasBPlotBeenCompleted, hasUnsavedChanges } =
              state;
            return {
              planningState,
              narrativeState,
              hasBPlotBeenCompleted,
              hasUnsavedChanges,
            };
          },
          // Equality function to prevent unnecessary undo states
          equality: (a, b) => {
            return JSON.stringify(a) === JSON.stringify(b);
          },
        }
      ),
      {
        // Persist configuration
        name: 'app-store',
        partialize: (state) => {
          // Only persist data, not functions
          const {
            planningState,
            narrativeState,
            hasBPlotBeenCompleted,
            hasUnsavedChanges,
            flow,
            projects,
            currentProjectId,
          } = state;
          return {
            planningState,
            narrativeState: serializeNarrativeState(narrativeState),
            hasBPlotBeenCompleted,
            hasUnsavedChanges,
            flow: flow ? serializeFlowState(flow) : undefined,
            projects,
            currentProjectId,
            version: STATE_VERSION,
          };
        },
        onRehydrateStorage: () => (state, error) => {
          if (error) {
            console.error('Failed to rehydrate store:', error);
            return;
          }

          if (state && (!isPersistedState(state) || state.version !== STATE_VERSION)) {
            state = migratePersistedState(
              state as unknown as PersistedState
            ) as unknown as AppStore;
          }

          if (state?.projects) {
            Object.values(state.projects).forEach((p: any) => {
              if (p.flowState && !p.flowState.completedSteps?.has) {
                try {
                  p.flowState = deserializeFlowState(p.flowState as any);
                } catch (e) {
                  console.error('Failed to deserialize project flow state:', e);
                  p.flowState = createInitialFlowState();
                }
              }

              // Ensure dates are properly converted
              p.createdAt = p.createdAt ? new Date(p.createdAt) : new Date();
              p.lastModified = p.lastModified ? new Date(p.lastModified) : new Date();

              // Validate dates - if invalid, use current date
              if (isNaN(p.createdAt.getTime())) {
                p.createdAt = new Date();
              }
              if (isNaN(p.lastModified.getTime())) {
                p.lastModified = new Date();
              }
            });
          }

          // Deserialize flow state
          if (state?.flow && !state.flow.completedSteps?.has) {
            try {
              state.flow = deserializeFlowState(state.flow as any);
            } catch (e) {
              console.error('Failed to deserialize flow state:', e);
              // Reset to initial flow state if deserialization fails
              state.flow = createInitialFlowState();
            }
          }

          // Deserialize narrative state
          if (state?.narrativeState) {
            try {
              state.narrativeState = deserializeNarrativeState(state.narrativeState);
            } catch (e) {
              console.error('Failed to deserialize narrative state:', e);
              // Initialize fresh narrative state if deserialization fails
              state.narrativeState = initializeNarrativeState();
            }
          }

          // Validate narrative state
          if (state?.narrativeState) {
            try {
              const validatedNarrativeState = recoverState<any>(
                state.narrativeState,
                validateNarrativeState,
                'narrative state (rehydration)'
              );
              state.narrativeState = validatedNarrativeState;
            } catch (error) {
              console.error('Failed to validate narrative state during rehydration:', error);
              // Use initial state as fallback
              state.narrativeState = initializeNarrativeState();
            }
          }

          // Migrate and validate planning state
          if (state?.planningState) {
            try {
              // Migrate old fields first
              const planningState = state.planningState as any;
              if (planningState.location && !planningState.locationAtmosphere) {
                planningState.locationAtmosphere = planningState.location;
              }
              // Remove deprecated fields
              delete planningState.location;
              delete planningState.underlyingReason;
              delete planningState.behaviorsAndAims;

              // Validate and sanitize planning state
              const validatedPlanningState = recoverState<any>(
                planningState,
                validatePlanningState,
                'planning state (rehydration)'
              );

              state.planningState = validatedPlanningState;

              // After rehydrating from storage, ensure planning state is also in old localStorage format
              // for backward compatibility
              localStorage.setItem('planningState', JSON.stringify(validatedPlanningState));
            } catch (error) {
              console.error('Failed to validate planning state during rehydration:', error);
              // Use initial state as fallback
              state.planningState = initialPlanningState;
            }
          }

          // Also try to load from old localStorage if store is empty
          if (!state?.planningState || Object.keys(state.planningState).length === 0) {
            try {
              const oldPlanningState = localStorage.getItem('planningState');
              if (oldPlanningState) {
                try {
                  const parsed = JSON.parse(oldPlanningState);
                  if (parsed && Object.keys(parsed).length > 0) {
                    // Migrate old fields
                    if (parsed.location && !parsed.locationAtmosphere) {
                      parsed.locationAtmosphere = parsed.location;
                    }
                    delete parsed.location;
                    delete parsed.underlyingReason;
                    delete parsed.behaviorsAndAims;

                    console.log('Loading planning state from old localStorage');
                    useAppStore.setState({ planningState: parsed });
                  }
                } catch (parseError) {
                  console.error('Failed to parse old planning state JSON:', parseError);
                  // Clear corrupted data
                  localStorage.removeItem('planningState');
                }
              }
            } catch (e) {
              console.error('Failed to load old planning state:', e);
            }
          }

          // Validate and restore flow state
          if (state?.flowActions) {
            state.flowActions.validateAndRestoreFlow();
          }
        },
      }
    ),
    {
      // Redux DevTools configuration
      name: 'Interactive Flow Tool',
    }
  )
);

// Export temporal controls for undo/redo
export const temporalControls = useAppStore.temporal;

// Initialize projects on load
useAppStore.getState().initializeProjects();
